import React, { Component } from 'react';
import classNames from 'classnames';
import {
  HashRouter,
  BrowserRouter as WebRouter,
  Switch,
  Route,
} from 'react-router-dom';
import { forEach } from '@grubhubprod/umami-library/dist/nodash/forEach';
import { CookbookProvider, grubhubLight } from '@grubhubprod/gh-cookbook-react';
import { NotificationsManager } from 'src/components/NotificationsManager/NotificationsManager';
import {
  Notification,
  NOTIFICATION_ACTIONS,
} from 'src/shared/services/NotificationService';
import { EventsManagerImpl } from 'src/shared/utilities/events-manager.utility';
import { SearchQueryService } from 'src/shared/services/search-query.service';
import { CredentialServiceImpl } from 'src/shared/services/credential-service';
import { apiClient } from 'src/shared/services/NativeApiClient';
import { CredentialsResponse } from 'src/utilities/types';
import { appConfigFactory } from 'src/shared/factories/appConfig.factory';
import { NativeMessages, ExperimentOverridePayload } from 'src/native-messages';
import { Environment, Platform } from 'src/environment';
import 'src/style/index.scss';
import type { RouteComponentProps } from 'src/shared/router';
import { windowFactory } from 'src/shared/factories';
import { AsyncRoute, RouteProps } from 'src/components/AsyncRoute/AsyncRoute';
import { getRoutes } from 'src/routes/Routes';
import type { FeatureToggleService } from 'src/shared/toggles';
import { ToggleContextProvider } from 'src/shared/toggles';
import { NativeBridgeImpl } from 'src/shared/utilities/native-bridge.utility';
import { lazyInject, container } from 'src/shared/di';
import { TYPES } from 'src/shared/di/types';
import { MonitorService } from 'src/shared/services';
import { DevBanner } from 'src/components/DevBanner/DevBanner';
import { LocalStorageUtility } from 'src/shared/utilities/localStorage.utility';
import {
  AnalyticsEventData,
  AnalyticsEvents,
  AnalyticsService,
  AnalyticsType,
} from 'src/shared/analytics/types';

export interface AppState {
  credentialsReceived: boolean;
  isAnonymous: boolean;
  notifications: Notification[];
  experiments: string[];
}

export class App extends Component<any, AppState> {
  private readonly appConfig = appConfigFactory();
  private readonly windowInstance = windowFactory();
  private readonly nativeBridge = NativeBridgeImpl.getInstance();
  private readonly searchQueryService = new SearchQueryService();
  private readonly eventsManager = EventsManagerImpl.getInstance();
  private readonly credentialService = CredentialServiceImpl.getInstance();
  private readonly umamiStorage = new LocalStorageUtility({
    storagePrefix: 'ngStorage',
  });
  @lazyInject(TYPES.MonitorService)
  private readonly _monitorService: MonitorService;
  @lazyInject(TYPES.FeatureToggleService)
  private readonly _featureToggleService: FeatureToggleService;
  private unsubscribeCalls: Function[] = [];
  @lazyInject(TYPES.AnalyticsServiceFactory)
  private readonly _analyticsServiceFactory: () => AnalyticsService;

  private get monitorService(): MonitorService {
    return (
      this._monitorService ||
      container.get<MonitorService>(TYPES.MonitorService)
    );
  }
  private get featureToggleService(): FeatureToggleService {
    return (
      this._featureToggleService ||
      container.get<FeatureToggleService>(TYPES.FeatureToggleService)
    );
  }
  private get analyticsServiceFactory(): () => AnalyticsService {
    return (
      this._analyticsServiceFactory ||
      container.get<() => AnalyticsService>(TYPES.AnalyticsServiceFactory)
    );
  }

  private _analyticsService: AnalyticsService | null = null;

  private get analyticsService(): AnalyticsService {
    if (!this._analyticsService) {
      this._analyticsService = this.analyticsServiceFactory();
    }
    return this._analyticsService;
  }

  public state = {
    credentialsReceived: false,
    isAnonymous: true,
    notifications: [],
    experiments: [],
  };

  constructor(props) {
    super(props);
    this.checkSearchQuery();
  }

  public async componentDidMount(): Promise<void> {
    await this.onInit();
  }

  public componentDidCatch(error): void {
    if (this.monitorService) {
      this.monitorService.reportError(error);
    } else {
      console.error('Error caught:', error);
    }
  }

  public componentWillUnmount(): void {
    this.unsubscribeCalls.forEach((unsubscribe) => unsubscribe());
    this.unsubscribeCalls = [];
    this.credentialService.unsubscribeToNativeSetCredentialsMessage();
    this.unsubscribeToExperiments();
  }

  private onInit(): void {
    if (this.appConfig.platform !== Platform.WEB) {
      // Native app flow - credentials come from native bridge
      this.credentialService
        .getCredentialsAsync()
        .then(this.updateStateFromCredentials);

      this.credentialService.subscribeToNativeSetCredentialsMessage(
        this.updateStateFromCredentials,
      );

      this.subscribeToPopEvents();
      this.subscribeToExperiments();

      this.nativeBridge.toNative(NativeMessages.ON_INIT);
    } else {
      // Web app flow - restore session from authToken if present
      this.restoreSession().then(() => {
        const persistedEvents = this.umamiStorage.get('analytics-events');

        if (persistedEvents) {
          this.handleStoredEvents(persistedEvents);
        }
      });
    }

    this.subscribeToNotificationEvents();

    this.verifyUserPreferences();
  }

  /**
   * Restores user session on web platform by fetching credentials from /session endpoint
   * Called on app initialization to populate credential service with user data
   */
  private async restoreSession(): Promise<void> {
    try {
      // Attempt to fetch current session using stored auth token
      const credentials = await apiClient.get<CredentialsResponse>('/session');

      // Store credentials in memory
      this.credentialService.setCredentials(credentials);
      this.updateStateFromCredentials();
    } catch (error) {
      // Session restoration failed (token invalid, expired, or not present)
      // User will be treated as anonymous
      console.log('Session restoration failed, user will be anonymous');
      this.credentialService.clearCredentials();
    }
  }

  private async verifyUserPreferences(): Promise<void> {
    const optOutFlag = this.credentialService.getCookieOptoutFlag();

    if (optOutFlag) {
      this.windowInstance.optOutRequestUpdated?.();
    }
  }

  private subscribeToNotificationEvents() {
    this.unsubscribeCalls.push(
      this.eventsManager.subscribe(
        NOTIFICATION_ACTIONS.update,
        this.handleNotificationAction,
      ),
    );
  }

  private handleNotificationAction = ({
    notifications,
  }: {
    notifications: Notification[];
  }): void => {
    this.setState({
      notifications,
    });
  };

  private handleStoredEvents(persistedEvents: Array<AnalyticsEventData>): void {
    forEach(persistedEvents, (event: AnalyticsEventData) => {
      if (event.type && event?.data?.payload) {
        const {
          type,
          data: { payload },
        } = event;
        switch (type) {
          case AnalyticsEvents.CLICKSTREAM_EVENT: {
            const csData = payload?.v2?.data;
            this.analyticsService.logAnalytics(
              { type: AnalyticsType.CLICKSTREAM },
              csData,
            );
            break;
          }
          case AnalyticsEvents.TEALIUM_EVENT: {
            this.analyticsService.logAnalytics(
              { type: AnalyticsType.GOOGLE },
              payload,
            );
            break;
          }
        }
      }
    });
    this.umamiStorage.remove('analytics-events');
  }

  private checkSearchQuery(): void {
    const searchQuery = this.searchQueryService.getSearchQuery();

    if (searchQuery) {
      this.searchQueryService.setSearchQuery(searchQuery);
    }
  }

  private subscribeToPopEvents(): void {
    this.unsubscribeCalls.push(
      this.eventsManager.subscribe(NativeMessages.NAVIGATE_BACK, () =>
        this.navigateBack(),
      ),
    );
  }

  private unsubscribeToExperiments(): void {
    const { experiments } = this.state;
    experiments.forEach((experiment) =>
      this.featureToggleService.removeLocalToggleValue(experiment),
    );
  }

  private subscribeToExperiments(): void {
    const experiments = [];
    this.unsubscribeCalls.push(
      this.eventsManager.subscribe(
        NativeMessages.SET_EXPERIMENTS,
        (payload: ExperimentOverridePayload) => {
          (payload.experiments || []).forEach((experiment) => {
            this.featureToggleService.setLocalToggleValue(experiment.name, {
              value: experiment.value,
            });
            experiments.push(experiment.name);
          });
        },
      ),
    );

    this.setState({
      experiments,
    });
  }

  private navigateBack() {
    history.back();
  }

  private initializeUserOnMonitors(): void {
    if (this.monitorService) {
      this.monitorService.initializeUser();
    }
  }

  private setCredentials = (credentials: any): void => {
    this.credentialService.setCredentials(credentials);
    this.updateStateFromCredentials();
  };

  public updateStateFromCredentials = (): void => {
    this.setState(
      {
        credentialsReceived: true,
        isAnonymous: this.credentialService.isAnonymous(),
      },
      () => {
        this.initializeUserOnMonitors();
      },
    );
  };

  private getRouteProps(): RouteProps {
    const { credentialsReceived, isAnonymous } = this.state;

    return {
      isAnonymous,
      credentialsReceived,
      setCredentials: this.setCredentials,
      platform: this.appConfig.platform,
      isAuthenticated: credentialsReceived && !isAnonymous,
    };
  }

  private renderSwitch() {
    return (
      <Switch>
        {Object.entries(getRoutes(this.appConfig)).map(
          ([path, routeConfig]) => {
            return (
              <Route
                key={path}
                path={path}
                render={(props: RouteComponentProps) => (
                  <AsyncRoute
                    key={`async-route-${path}`}
                    {...props}
                    {...this.getRouteProps()}
                    module={routeConfig.module}
                    export={routeConfig.export}
                  />
                )}
              />
            );
          },
        )}
      </Switch>
    );
  }

  private getRouterType() {
    return this.windowInstance.location.pathname === '/' &&
      !!this.windowInstance.location.hash
      ? HashRouter
      : WebRouter;
  }

  public render() {
    const Router = this.getRouterType();
    const { notifications } = this.state;
    const { brand, environment } = this.appConfig;

    const classes = classNames(`brand-${brand.toLowerCase()}`, {
      [`env-${environment.toLowerCase()}`]: environment !== Environment.PROD,
    });

    return (
      <div id="app" className={classes}>
        <CookbookProvider theme={grubhubLight}>
          <NotificationsManager notifications={notifications} />
          <Router>
            <ToggleContextProvider monitorService={this.monitorService}>
              {this.renderSwitch()}
            </ToggleContextProvider>
            <DevBanner />
          </Router>
        </CookbookProvider>
      </div>
    );
  }
}
